import * as Schema from "effect/Schema";
import { EventId, IsoDateTime, NonNegativeInt, ProviderItemId, PositiveInt, RuntimeItemId, RuntimeTaskId, ThreadId, TrimmedNonEmptyString, TurnId, } from "./baseSchemas.js";
import { ProviderInstanceId, ProviderDriverKind } from "./providerInstance.js";
const TrimmedNonEmptyStringSchema = TrimmedNonEmptyString;
const UnknownRecordSchema = Schema.Record(Schema.String, Schema.Unknown);
const RuntimeEventRawSource = Schema.Union([
    Schema.Literal("codex.app-server.notification"),
    Schema.Literal("codex.eventmsg"),
    Schema.Literal("claude.sdk.message"),
    Schema.Literal("codex.sdk.thread-event"),
    Schema.Literal("opencode.sdk.event"),
    Schema.Literal("acp.jsonrpc"),
    Schema.TemplateLiteral(["acp.", Schema.String, ".extension"]),
]);
export const RuntimeEventRaw = Schema.Struct({
    source: RuntimeEventRawSource,
    method: Schema.optional(TrimmedNonEmptyStringSchema),
    messageType: Schema.optional(TrimmedNonEmptyStringSchema),
    payload: Schema.Unknown,
});
const ProviderRefs = Schema.Struct({
    providerTurnId: Schema.optional(TrimmedNonEmptyStringSchema),
    providerItemId: Schema.optional(ProviderItemId),
});
const RuntimeSessionState = Schema.Literals([
    "starting",
    "ready",
    "running",
    "waiting",
    "stopped",
    "error",
]);
const RuntimeThreadState = Schema.Literals(["active", "idle", "archived", "closed", "error"]);
const RuntimeTurnState = Schema.Literals(["completed", "failed", "interrupted", "cancelled"]);
const RuntimePlanStepStatus = Schema.Literals(["pending", "inProgress", "completed"]);
const RuntimeItemStatus = Schema.Literals(["inProgress", "completed", "failed", "declined"]);
const RuntimeContentStreamKind = Schema.Literals([
    "assistant_text",
    "reasoning_text",
    "reasoning_summary_text",
    "plan_text",
    "command_output",
    "file_change_output",
    "unknown",
]);
const RuntimeSessionExitKind = Schema.Literals(["graceful", "error"]);
const RuntimeErrorClass = Schema.Literals([
    "provider_error",
    "transport_error",
    "permission_error",
    "validation_error",
    "unknown",
]);
/**
 * How a turn's inference failed, when the cause is one the user must be able to
 * act on. The kind rather than a message crosses the wire so the client renders a
 * localized, distinct report for each.
 *
 * `proxy_unreachable` and `update_required` are not refusals by the inference
 * service and must not read as though they were: neither is anything to do with
 * the user's account, and an unreachable service in particular must never suggest
 * using a different provider - there is none.
 *
 * `request_too_large` is the one kind the user answers inside the turn: the
 * request exceeded the size the inference service accepts, which in practice
 * means attached images.
 *
 * `request_rejected` is the service refusing a request it will not serve on any
 * provider account; resending it changes nothing, and the operator has the
 * provider's reason.
 *
 * `credential_missing` is a turn the server ran holding no inference credential,
 * so nothing reached the service. The client knows why it minted none and words
 * the report accordingly.
 */
export const InferenceFailureKind = Schema.Literals([
    "credential_rejected",
    "entitlement_inactive",
    "no_capacity",
    "usage_limit",
    "upstream_interrupted",
    "proxy_unreachable",
    "update_required",
    "request_too_large",
    "request_rejected",
    "credential_missing",
]);
export const TOOL_LIFECYCLE_ITEM_TYPES = [
    "command_execution",
    "file_change",
    "mcp_tool_call",
    "dynamic_tool_call",
    "collab_agent_tool_call",
    "web_search",
    "image_view",
];
export const ToolLifecycleItemType = Schema.Literals(TOOL_LIFECYCLE_ITEM_TYPES);
export function isToolLifecycleItemType(value) {
    return TOOL_LIFECYCLE_ITEM_TYPES.includes(value);
}
export const CanonicalItemType = Schema.Literals([
    "user_message",
    "assistant_message",
    "reasoning",
    "plan",
    ...TOOL_LIFECYCLE_ITEM_TYPES,
    "review_entered",
    "review_exited",
    "context_compaction",
    "error",
    "unknown",
]);
const ProviderRuntimeEventType = Schema.Literals([
    "session.started",
    "session.configured",
    "session.state.changed",
    "session.exited",
    "thread.started",
    "thread.state.changed",
    "thread.metadata.updated",
    "thread.token-usage.updated",
    "thread.realtime.started",
    "thread.realtime.item-added",
    "thread.realtime.audio.delta",
    "thread.realtime.error",
    "thread.realtime.closed",
    "turn.started",
    "turn.completed",
    "turn.aborted",
    "turn.plan.updated",
    "turn.diff.updated",
    "item.started",
    "item.updated",
    "item.completed",
    "content.delta",
    "task.started",
    "task.progress",
    "task.completed",
    "hook.started",
    "hook.progress",
    "hook.completed",
    "tool.progress",
    "tool.summary",
    "auth.status",
    "account.updated",
    "account.rate-limits.updated",
    "mcp.status.updated",
    "mcp.oauth.completed",
    "model.rerouted",
    "config.warning",
    "deprecation.notice",
    "files.persisted",
    "runtime.warning",
    "runtime.error",
]);
const SessionStartedType = Schema.Literal("session.started");
const SessionConfiguredType = Schema.Literal("session.configured");
const SessionStateChangedType = Schema.Literal("session.state.changed");
const SessionExitedType = Schema.Literal("session.exited");
const ThreadStartedType = Schema.Literal("thread.started");
const ThreadStateChangedType = Schema.Literal("thread.state.changed");
const ThreadMetadataUpdatedType = Schema.Literal("thread.metadata.updated");
const ThreadTokenUsageUpdatedType = Schema.Literal("thread.token-usage.updated");
const ThreadRealtimeStartedType = Schema.Literal("thread.realtime.started");
const ThreadRealtimeItemAddedType = Schema.Literal("thread.realtime.item-added");
const ThreadRealtimeAudioDeltaType = Schema.Literal("thread.realtime.audio.delta");
const ThreadRealtimeErrorType = Schema.Literal("thread.realtime.error");
const ThreadRealtimeClosedType = Schema.Literal("thread.realtime.closed");
const TurnStartedType = Schema.Literal("turn.started");
const TurnCompletedType = Schema.Literal("turn.completed");
const TurnAbortedType = Schema.Literal("turn.aborted");
const TurnPlanUpdatedType = Schema.Literal("turn.plan.updated");
const TurnDiffUpdatedType = Schema.Literal("turn.diff.updated");
const ItemStartedType = Schema.Literal("item.started");
const ItemUpdatedType = Schema.Literal("item.updated");
const ItemCompletedType = Schema.Literal("item.completed");
const ContentDeltaType = Schema.Literal("content.delta");
const TaskStartedType = Schema.Literal("task.started");
const TaskProgressType = Schema.Literal("task.progress");
const TaskCompletedType = Schema.Literal("task.completed");
const HookStartedType = Schema.Literal("hook.started");
const HookProgressType = Schema.Literal("hook.progress");
const HookCompletedType = Schema.Literal("hook.completed");
const ToolProgressType = Schema.Literal("tool.progress");
const ToolSummaryType = Schema.Literal("tool.summary");
const AuthStatusType = Schema.Literal("auth.status");
const AccountUpdatedType = Schema.Literal("account.updated");
const AccountRateLimitsUpdatedType = Schema.Literal("account.rate-limits.updated");
const McpStatusUpdatedType = Schema.Literal("mcp.status.updated");
const McpOauthCompletedType = Schema.Literal("mcp.oauth.completed");
const ModelReroutedType = Schema.Literal("model.rerouted");
const ConfigWarningType = Schema.Literal("config.warning");
const DeprecationNoticeType = Schema.Literal("deprecation.notice");
const FilesPersistedType = Schema.Literal("files.persisted");
const ToolDeniedType = Schema.Literal("tool.denied");
const RuntimeWarningType = Schema.Literal("runtime.warning");
const RuntimeErrorType = Schema.Literal("runtime.error");
const ProviderRuntimeEventBase = Schema.Struct({
    eventId: EventId,
    provider: ProviderDriverKind,
    // Optional during the driver/instance migration. See providerInstance.ts
    // for the routing-key-vs-driver-id distinction. Once every emitter
    // populates it (post-slice-4), routing flips to instance-id-only.
    providerInstanceId: Schema.optional(ProviderInstanceId),
    threadId: ThreadId,
    createdAt: IsoDateTime,
    turnId: Schema.optional(TurnId),
    itemId: Schema.optional(RuntimeItemId),
    providerRefs: Schema.optional(ProviderRefs),
    raw: Schema.optional(RuntimeEventRaw),
});
const SessionStartedPayload = Schema.Struct({
    message: Schema.optional(TrimmedNonEmptyStringSchema),
    resume: Schema.optional(Schema.Unknown),
});
const SessionConfiguredPayload = Schema.Struct({
    config: UnknownRecordSchema,
});
const SessionStateChangedPayload = Schema.Struct({
    state: RuntimeSessionState,
    reason: Schema.optional(TrimmedNonEmptyStringSchema),
    detail: Schema.optional(Schema.Unknown),
});
const SessionExitedPayload = Schema.Struct({
    reason: Schema.optional(TrimmedNonEmptyStringSchema),
    recoverable: Schema.optional(Schema.Boolean),
    exitKind: Schema.optional(RuntimeSessionExitKind),
});
const ThreadStartedPayload = Schema.Struct({
    providerThreadId: Schema.optional(TrimmedNonEmptyStringSchema),
});
const ThreadStateChangedPayload = Schema.Struct({
    state: RuntimeThreadState,
    detail: Schema.optional(Schema.Unknown),
});
const ThreadMetadataUpdatedPayload = Schema.Struct({
    name: Schema.optional(TrimmedNonEmptyStringSchema),
    metadata: Schema.optional(UnknownRecordSchema),
});
export const ThreadTokenUsageSnapshot = Schema.Struct({
    /** Context-window occupancy of the latest model request: its full prompt
     *  (cache reads included) plus its completion. Drops after compaction. */
    usedTokens: NonNegativeInt,
    /** Cumulative unique tokens: each token counts once when it first enters
     *  the context window (sum of positive `usedTokens` deltas), so cache hits,
     *  misses, and re-processing never move it. */
    totalProcessedTokens: Schema.optional(NonNegativeInt),
    /** Effective context window of the thread's model. */
    maxTokens: Schema.optional(PositiveInt),
    inputTokens: Schema.optional(NonNegativeInt),
    /** Cache-read slice of `inputTokens` (subset, not an addend). */
    cachedInputTokens: Schema.optional(NonNegativeInt),
    outputTokens: Schema.optional(NonNegativeInt),
    reasoningOutputTokens: Schema.optional(NonNegativeInt),
    lastUsedTokens: Schema.optional(NonNegativeInt),
    lastInputTokens: Schema.optional(NonNegativeInt),
    lastCachedInputTokens: Schema.optional(NonNegativeInt),
    lastOutputTokens: Schema.optional(NonNegativeInt),
    lastReasoningOutputTokens: Schema.optional(NonNegativeInt),
    toolUses: Schema.optional(NonNegativeInt),
    durationMs: Schema.optional(NonNegativeInt),
    compactsAutomatically: Schema.optional(Schema.Boolean),
});
const ThreadTokenUsageUpdatedPayload = Schema.Struct({
    usage: ThreadTokenUsageSnapshot,
});
const ThreadRealtimeStartedPayload = Schema.Struct({
    realtimeSessionId: Schema.optional(TrimmedNonEmptyStringSchema),
});
const ThreadRealtimeItemAddedPayload = Schema.Struct({
    item: Schema.Unknown,
});
const ThreadRealtimeAudioDeltaPayload = Schema.Struct({
    audio: Schema.Unknown,
});
const ThreadRealtimeErrorPayload = Schema.Struct({
    message: TrimmedNonEmptyStringSchema,
});
const ThreadRealtimeClosedPayload = Schema.Struct({
    reason: Schema.optional(TrimmedNonEmptyStringSchema),
});
const TurnStartedPayload = Schema.Struct({
    model: Schema.optional(TrimmedNonEmptyStringSchema),
    effort: Schema.optional(TrimmedNonEmptyStringSchema),
});
const TurnCompletedPayload = Schema.Struct({
    state: RuntimeTurnState,
    stopReason: Schema.optional(Schema.NullOr(TrimmedNonEmptyStringSchema)),
    usage: Schema.optional(Schema.Unknown),
    modelUsage: Schema.optional(UnknownRecordSchema),
    totalCostUsd: Schema.optional(Schema.Number),
    errorMessage: Schema.optional(TrimmedNonEmptyStringSchema),
    errorKind: Schema.optional(InferenceFailureKind),
});
const TurnAbortedPayload = Schema.Struct({
    reason: TrimmedNonEmptyStringSchema,
});
const RuntimePlanStep = Schema.Struct({
    step: TrimmedNonEmptyStringSchema,
    status: RuntimePlanStepStatus,
});
const TurnPlanUpdatedPayload = Schema.Struct({
    explanation: Schema.optional(Schema.NullOr(TrimmedNonEmptyStringSchema)),
    plan: Schema.Array(RuntimePlanStep),
});
const TurnDiffUpdatedPayload = Schema.Struct({
    unifiedDiff: Schema.String,
});
export const ItemLifecyclePayload = Schema.Struct({
    itemType: CanonicalItemType,
    status: Schema.optional(RuntimeItemStatus),
    title: Schema.optional(TrimmedNonEmptyStringSchema),
    detail: Schema.optional(TrimmedNonEmptyStringSchema),
    data: Schema.optional(Schema.Unknown),
});
const ContentDeltaPayload = Schema.Struct({
    streamKind: RuntimeContentStreamKind,
    delta: Schema.String,
    contentIndex: Schema.optional(Schema.Int),
    summaryIndex: Schema.optional(Schema.Int),
});
const TaskStartedPayload = Schema.Struct({
    taskId: RuntimeTaskId,
    description: Schema.optional(TrimmedNonEmptyStringSchema),
    taskType: Schema.optional(TrimmedNonEmptyStringSchema),
    prompt: Schema.optional(TrimmedNonEmptyStringSchema),
    /** Canonical id of the sub-agent's own thread, when its transcript is ingested. */
    childThreadId: Schema.optional(ThreadId),
});
/** Structured view of the sub-agent's current tool item, enough for activity labeling. */
export const TaskProgressItem = Schema.Struct({
    itemType: ToolLifecycleItemType,
    command: Schema.optional(Schema.String),
    server: Schema.optional(Schema.String),
    tool: Schema.optional(Schema.String),
    arguments: Schema.optional(Schema.Unknown),
    path: Schema.optional(Schema.String),
    changes: Schema.optional(Schema.Array(Schema.Struct({ path: Schema.String }))),
    query: Schema.optional(Schema.String),
});
const TaskProgressPayload = Schema.Struct({
    taskId: RuntimeTaskId,
    description: TrimmedNonEmptyStringSchema,
    summary: Schema.optional(TrimmedNonEmptyStringSchema),
    usage: Schema.optional(Schema.Unknown),
    lastToolName: Schema.optional(TrimmedNonEmptyStringSchema),
    childThreadId: Schema.optional(ThreadId),
    item: Schema.optional(TaskProgressItem),
});
const TaskCompletedPayload = Schema.Struct({
    taskId: RuntimeTaskId,
    status: Schema.Literals(["completed", "failed", "stopped"]),
    summary: Schema.optional(TrimmedNonEmptyStringSchema),
    usage: Schema.optional(Schema.Unknown),
    childThreadId: Schema.optional(ThreadId),
});
const HookStartedPayload = Schema.Struct({
    hookId: TrimmedNonEmptyStringSchema,
    hookName: TrimmedNonEmptyStringSchema,
    hookEvent: TrimmedNonEmptyStringSchema,
});
const HookProgressPayload = Schema.Struct({
    hookId: TrimmedNonEmptyStringSchema,
    output: Schema.optional(Schema.String),
    stdout: Schema.optional(Schema.String),
    stderr: Schema.optional(Schema.String),
});
const HookCompletedPayload = Schema.Struct({
    hookId: TrimmedNonEmptyStringSchema,
    outcome: Schema.Literals(["success", "error", "cancelled"]),
    output: Schema.optional(Schema.String),
    stdout: Schema.optional(Schema.String),
    stderr: Schema.optional(Schema.String),
    exitCode: Schema.optional(Schema.Int),
});
const ToolProgressPayload = Schema.Struct({
    toolUseId: Schema.optional(TrimmedNonEmptyStringSchema),
    toolName: Schema.optional(TrimmedNonEmptyStringSchema),
    summary: Schema.optional(TrimmedNonEmptyStringSchema),
    elapsedSeconds: Schema.optional(Schema.Number),
});
const ToolSummaryPayload = Schema.Struct({
    summary: TrimmedNonEmptyStringSchema,
    precedingToolUseIds: Schema.optional(Schema.Array(TrimmedNonEmptyStringSchema)),
});
const AuthStatusPayload = Schema.Struct({
    isAuthenticating: Schema.optional(Schema.Boolean),
    output: Schema.optional(Schema.Array(Schema.String)),
    error: Schema.optional(TrimmedNonEmptyStringSchema),
});
const AccountUpdatedPayload = Schema.Struct({
    account: Schema.Unknown,
});
const AccountRateLimitsUpdatedPayload = Schema.Struct({
    rateLimits: Schema.Unknown,
});
const McpStatusUpdatedPayload = Schema.Struct({
    status: Schema.Unknown,
});
const McpOauthCompletedPayload = Schema.Struct({
    success: Schema.Boolean,
    name: Schema.optional(TrimmedNonEmptyStringSchema),
    error: Schema.optional(TrimmedNonEmptyStringSchema),
});
const ModelReroutedPayload = Schema.Struct({
    fromModel: TrimmedNonEmptyStringSchema,
    toModel: TrimmedNonEmptyStringSchema,
    reason: TrimmedNonEmptyStringSchema,
});
const ConfigWarningPayload = Schema.Struct({
    summary: TrimmedNonEmptyStringSchema,
    details: Schema.optional(TrimmedNonEmptyStringSchema),
    path: Schema.optional(TrimmedNonEmptyStringSchema),
    range: Schema.optional(Schema.Unknown),
});
const DeprecationNoticePayload = Schema.Struct({
    summary: TrimmedNonEmptyStringSchema,
    details: Schema.optional(TrimmedNonEmptyStringSchema),
});
const FilesPersistedPayload = Schema.Struct({
    files: Schema.Array(Schema.Struct({
        filename: TrimmedNonEmptyStringSchema,
        fileId: TrimmedNonEmptyStringSchema,
    })),
    failed: Schema.optional(Schema.Array(Schema.Struct({
        filename: TrimmedNonEmptyStringSchema,
        error: TrimmedNonEmptyStringSchema,
    }))),
});
const ToolDeniedPayload = Schema.Struct({
    toolName: TrimmedNonEmptyStringSchema,
    toolUseId: Schema.optional(TrimmedNonEmptyStringSchema),
    reason: Schema.optional(TrimmedNonEmptyStringSchema),
    agentId: Schema.optional(TrimmedNonEmptyStringSchema),
});
const RuntimeWarningPayload = Schema.Struct({
    message: TrimmedNonEmptyStringSchema,
    detail: Schema.optional(Schema.Unknown),
});
const RuntimeErrorPayload = Schema.Struct({
    message: TrimmedNonEmptyStringSchema,
    class: Schema.optional(RuntimeErrorClass),
    kind: Schema.optional(InferenceFailureKind),
    detail: Schema.optional(Schema.Unknown),
});
const ProviderRuntimeSessionStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: SessionStartedType,
    payload: SessionStartedPayload,
});
const ProviderRuntimeSessionConfiguredEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: SessionConfiguredType,
    payload: SessionConfiguredPayload,
});
const ProviderRuntimeSessionStateChangedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: SessionStateChangedType,
    payload: SessionStateChangedPayload,
});
const ProviderRuntimeSessionExitedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: SessionExitedType,
    payload: SessionExitedPayload,
});
const ProviderRuntimeThreadStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadStartedType,
    payload: ThreadStartedPayload,
});
const ProviderRuntimeThreadStateChangedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadStateChangedType,
    payload: ThreadStateChangedPayload,
});
const ProviderRuntimeThreadMetadataUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadMetadataUpdatedType,
    payload: ThreadMetadataUpdatedPayload,
});
const ProviderRuntimeThreadTokenUsageUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadTokenUsageUpdatedType,
    payload: ThreadTokenUsageUpdatedPayload,
});
const ProviderRuntimeThreadRealtimeStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadRealtimeStartedType,
    payload: ThreadRealtimeStartedPayload,
});
const ProviderRuntimeThreadRealtimeItemAddedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadRealtimeItemAddedType,
    payload: ThreadRealtimeItemAddedPayload,
});
const ProviderRuntimeThreadRealtimeAudioDeltaEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadRealtimeAudioDeltaType,
    payload: ThreadRealtimeAudioDeltaPayload,
});
const ProviderRuntimeThreadRealtimeErrorEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadRealtimeErrorType,
    payload: ThreadRealtimeErrorPayload,
});
const ProviderRuntimeThreadRealtimeClosedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ThreadRealtimeClosedType,
    payload: ThreadRealtimeClosedPayload,
});
const ProviderRuntimeTurnStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TurnStartedType,
    payload: TurnStartedPayload,
});
const ProviderRuntimeTurnCompletedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TurnCompletedType,
    payload: TurnCompletedPayload,
});
const ProviderRuntimeTurnAbortedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TurnAbortedType,
    payload: TurnAbortedPayload,
});
const ProviderRuntimeTurnPlanUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TurnPlanUpdatedType,
    payload: TurnPlanUpdatedPayload,
});
const ProviderRuntimeTurnDiffUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TurnDiffUpdatedType,
    payload: TurnDiffUpdatedPayload,
});
const ProviderRuntimeItemStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ItemStartedType,
    payload: ItemLifecyclePayload,
});
const ProviderRuntimeItemUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ItemUpdatedType,
    payload: ItemLifecyclePayload,
});
const ProviderRuntimeItemCompletedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ItemCompletedType,
    payload: ItemLifecyclePayload,
});
const ProviderRuntimeContentDeltaEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ContentDeltaType,
    payload: ContentDeltaPayload,
});
const ProviderRuntimeTaskStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TaskStartedType,
    payload: TaskStartedPayload,
});
const ProviderRuntimeTaskProgressEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TaskProgressType,
    payload: TaskProgressPayload,
});
const ProviderRuntimeTaskCompletedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: TaskCompletedType,
    payload: TaskCompletedPayload,
});
const ProviderRuntimeHookStartedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: HookStartedType,
    payload: HookStartedPayload,
});
const ProviderRuntimeHookProgressEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: HookProgressType,
    payload: HookProgressPayload,
});
const ProviderRuntimeHookCompletedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: HookCompletedType,
    payload: HookCompletedPayload,
});
const ProviderRuntimeToolProgressEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ToolProgressType,
    payload: ToolProgressPayload,
});
const ProviderRuntimeToolSummaryEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ToolSummaryType,
    payload: ToolSummaryPayload,
});
const ProviderRuntimeAuthStatusEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: AuthStatusType,
    payload: AuthStatusPayload,
});
const ProviderRuntimeAccountUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: AccountUpdatedType,
    payload: AccountUpdatedPayload,
});
const ProviderRuntimeAccountRateLimitsUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: AccountRateLimitsUpdatedType,
    payload: AccountRateLimitsUpdatedPayload,
});
const ProviderRuntimeMcpStatusUpdatedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: McpStatusUpdatedType,
    payload: McpStatusUpdatedPayload,
});
const ProviderRuntimeMcpOauthCompletedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: McpOauthCompletedType,
    payload: McpOauthCompletedPayload,
});
const ProviderRuntimeModelReroutedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ModelReroutedType,
    payload: ModelReroutedPayload,
});
const ProviderRuntimeConfigWarningEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ConfigWarningType,
    payload: ConfigWarningPayload,
});
const ProviderRuntimeDeprecationNoticeEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: DeprecationNoticeType,
    payload: DeprecationNoticePayload,
});
const ProviderRuntimeFilesPersistedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: FilesPersistedType,
    payload: FilesPersistedPayload,
});
const ProviderRuntimeToolDeniedEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: ToolDeniedType,
    payload: ToolDeniedPayload,
});
const ProviderRuntimeWarningEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: RuntimeWarningType,
    payload: RuntimeWarningPayload,
});
const ProviderRuntimeErrorEvent = Schema.Struct({
    ...ProviderRuntimeEventBase.fields,
    type: RuntimeErrorType,
    payload: RuntimeErrorPayload,
});
export const ProviderRuntimeEventV2 = Schema.Union([
    ProviderRuntimeSessionStartedEvent,
    ProviderRuntimeSessionConfiguredEvent,
    ProviderRuntimeSessionStateChangedEvent,
    ProviderRuntimeSessionExitedEvent,
    ProviderRuntimeThreadStartedEvent,
    ProviderRuntimeThreadStateChangedEvent,
    ProviderRuntimeThreadMetadataUpdatedEvent,
    ProviderRuntimeThreadTokenUsageUpdatedEvent,
    ProviderRuntimeThreadRealtimeStartedEvent,
    ProviderRuntimeThreadRealtimeItemAddedEvent,
    ProviderRuntimeThreadRealtimeAudioDeltaEvent,
    ProviderRuntimeThreadRealtimeErrorEvent,
    ProviderRuntimeThreadRealtimeClosedEvent,
    ProviderRuntimeTurnStartedEvent,
    ProviderRuntimeTurnCompletedEvent,
    ProviderRuntimeTurnAbortedEvent,
    ProviderRuntimeTurnPlanUpdatedEvent,
    ProviderRuntimeTurnDiffUpdatedEvent,
    ProviderRuntimeItemStartedEvent,
    ProviderRuntimeItemUpdatedEvent,
    ProviderRuntimeItemCompletedEvent,
    ProviderRuntimeContentDeltaEvent,
    ProviderRuntimeTaskStartedEvent,
    ProviderRuntimeTaskProgressEvent,
    ProviderRuntimeTaskCompletedEvent,
    ProviderRuntimeHookStartedEvent,
    ProviderRuntimeHookProgressEvent,
    ProviderRuntimeHookCompletedEvent,
    ProviderRuntimeToolProgressEvent,
    ProviderRuntimeToolSummaryEvent,
    ProviderRuntimeAuthStatusEvent,
    ProviderRuntimeAccountUpdatedEvent,
    ProviderRuntimeAccountRateLimitsUpdatedEvent,
    ProviderRuntimeMcpStatusUpdatedEvent,
    ProviderRuntimeMcpOauthCompletedEvent,
    ProviderRuntimeModelReroutedEvent,
    ProviderRuntimeConfigWarningEvent,
    ProviderRuntimeDeprecationNoticeEvent,
    ProviderRuntimeFilesPersistedEvent,
    ProviderRuntimeToolDeniedEvent,
    ProviderRuntimeWarningEvent,
    ProviderRuntimeErrorEvent,
]);
export const ProviderRuntimeEvent = ProviderRuntimeEventV2;
// Compatibility aliases for call sites still importing legacy names.
const ProviderRuntimeMessageDeltaEvent = ProviderRuntimeContentDeltaEvent;
const ProviderRuntimeMessageCompletedEvent = ProviderRuntimeItemCompletedEvent;
const ProviderRuntimeToolStartedEvent = ProviderRuntimeItemStartedEvent;
const ProviderRuntimeToolCompletedEvent = ProviderRuntimeItemCompletedEvent;
// Legacy helper aliases retained for adapters/tests.
const ProviderRuntimeToolKind = Schema.Literals(["command", "file-read", "file-change", "other"]);
export const ProviderRuntimeTurnStatus = RuntimeTurnState;
